import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { PaymentProviderLogo, PaymentProviderLabel, getProviderFromMethod } from '../components/PaymentProviderLogo';
import { PaymentMethodForm, validatePaymentMethod } from '../components/PaymentMethodForm';
import SafeImage from '../components/SafeImage';
import { ArrowLeft, Plus, X, Star, Edit2, ExternalLink, Eye, User, Phone, Tag } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_METHOD = {
  id: '', method: '', payment_method_type: 'link', payment_provider: null,
  handle: '', url: '', is_auto_generated: false, recipient_name: '',
  email_or_phone: '', qr_code_image: '', suggested_memo: '', instructions: '',
  is_default: false,
};

export default function PaymentMethods({ user }) {
  const navigate = useNavigate();
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newMethod, setNewMethod] = useState({ ...EMPTY_METHOD });
  const [editingId, setEditingId] = useState(null);
  const [editMethod, setEditMethod] = useState(null);
  const [testModal, setTestModal] = useState(null);

  const token = localStorage.getItem('session_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchPaymentMethods(); }, []);

  const fetchPaymentMethods = async () => {
    try {
      const res = await axios.get(`${API}/users/payment-methods`, { headers });
      setMethods(res.data);
    } catch { toast.error('Failed to fetch payment methods'); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    const err = validatePaymentMethod(newMethod);
    if (err) { toast.error(err); return; }
    try {
      const toAdd = { ...newMethod, id: `pm_${Date.now()}` };
      await axios.post(`${API}/users/payment-methods`, toAdd, { headers });
      toast.success('Payment method added');
      setNewMethod({ ...EMPTY_METHOD });
      setShowAdd(false);
      fetchPaymentMethods();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to add payment method');
    }
  };

  const handleEdit = (m) => {
    setEditingId(m.id);
    setEditMethod({
      ...EMPTY_METHOD,
      ...Object.fromEntries(Object.entries(m).map(([k, v]) => [k, v ?? ''])),
      payment_provider: m.payment_provider || getProviderFromMethod(m.method) || 'custom',
    });
  };

  const handleSaveEdit = async () => {
    const err = validatePaymentMethod(editMethod);
    if (err) { toast.error(err); return; }
    try {
      const payload = Object.fromEntries(
        Object.entries(editMethod).map(([k, v]) => [k, v ?? ''])
      );
      await axios.put(`${API}/users/payment-methods/${editMethod.id}`, payload, { headers });
      toast.success('Payment method updated');
      setEditingId(null);
      setEditMethod(null);
      fetchPaymentMethods();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update payment method');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this payment method?')) return;
    try {
      await axios.delete(`${API}/users/payment-methods/${id}`, { headers });
      toast.success('Deleted');
      fetchPaymentMethods();
    } catch { toast.error('Failed to delete'); }
  };

  const handleTest = (m) => {
    const provider = m.payment_provider || getProviderFromMethod(m.method);
    const isZelle = provider === 'zelle';
    const isDirect = m.payment_method_type === 'direct' || isZelle;
    if (!isDirect && m.url) {
      window.open(m.url, '_blank');
    } else {
      setTestModal(m);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="w-10 h-10 border-3 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="border-b border-[#1A1A1A] bg-[#0A0A0A]">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" className="mb-4 -ml-2 text-[#EAE7E1] hover:text-[#D4AF37]" data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Dashboard
          </Button>
          <h1 className="text-2xl font-bold text-[#EAE7E1] tracking-tight">Payment Methods</h1>
          <p className="mt-1 text-sm text-[#AFAFAF]">Saved templates — imported into your events during creation</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Add button */}
        {!showAdd && (
          <button
            onClick={() => setShowAdd(true)}
            className="w-full mb-6 flex items-center justify-center gap-2 py-3.5 border-2 border-dashed border-[#C6A66B]/30 rounded-sm text-sm font-semibold text-[#C6A66B] hover:bg-[#C6A66B]/5 transition-colors"
            data-testid="add-payment-method-button"
          >
            <Plus className="w-4 h-4" /> Add Payment Method
          </button>
        )}

        {/* Add form */}
        {showAdd && (
          <div className="mb-6 bg-[#0A0A0A] border border-[#C6A66B]/30 rounded-sm p-5" data-testid="add-method-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-[#EAE7E1]">Add Payment Method</h3>
              <button onClick={() => { setShowAdd(false); setNewMethod({ ...EMPTY_METHOD }); }} className="text-[#777] hover:text-[#EAE7E1]"><X className="w-4 h-4" /></button>
            </div>
            <PaymentMethodForm method={newMethod} onChange={setNewMethod} />
            <Button onClick={handleAdd} className="w-full mt-4 bg-[#C6A66B] hover:bg-[#D4AF37] text-[#050505] font-bold rounded-sm py-3" data-testid="save-method-button">
              Save Method
            </Button>
          </div>
        )}

        {/* Methods list */}
        {methods.length === 0 && !showAdd ? (
          <div className="text-center py-12 border border-dashed border-[#1A1A1A] rounded-sm">
            <p className="text-sm text-[#777] mb-3">No payment methods saved yet</p>
            <Button onClick={() => setShowAdd(true)} variant="outline" className="border-[#D4AF37] text-[#D4AF37]" data-testid="add-first-method-button">
              Add Your First Method
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="methods-list">
            {methods.map(m => {
              const provider = m.payment_provider || getProviderFromMethod(m.method);
              const isZelle = provider === 'zelle';
              const isDirect = m.payment_method_type === 'direct' || isZelle;

              if (editingId === m.id) {
                return (
                  <div key={m.id} className="bg-[#0A0A0A] border border-[#C6A66B]/30 rounded-sm p-5" data-testid={`edit-method-form-${m.id}`}>
                    <h3 className="text-sm font-semibold text-[#EAE7E1] mb-3">Edit Payment Method</h3>
                    <PaymentMethodForm method={editMethod} onChange={setEditMethod} />
                    <div className="flex gap-2 mt-4">
                      <Button onClick={handleSaveEdit} className="flex-1 bg-[#C6A66B] hover:bg-[#D4AF37] text-[#050505] font-bold rounded-sm" data-testid="save-edit-method-button">Save</Button>
                      <Button onClick={() => { setEditingId(null); setEditMethod(null); }} variant="outline" className="flex-1 border-[#1A1A1A] text-[#777]" data-testid="cancel-edit-method-button">Cancel</Button>
                    </div>
                  </div>
                );
              }

              return (
                <div key={m.id} className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-4 hover:border-[#333] transition-colors" data-testid={`method-card-${m.id}`}>
                  <div className="flex items-start gap-3">
                    <PaymentProviderLogo provider={provider} method={m.method} size="lg" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <PaymentProviderLabel provider={provider} method={m.method} />
                        {m.is_default && (
                          <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-[#D4AF37]/15 text-[#D4AF37]">Default</span>
                        )}
                      </div>
                      {m.handle && <div className="text-sm text-[#AFAFAF] mt-0.5">{m.handle}</div>}
                      {!isDirect && m.url && <div className="text-xs text-[#777] truncate mt-0.5">{m.url}</div>}
                      {isDirect && m.recipient_name && <div className="text-sm text-[#AFAFAF] mt-0.5">{m.recipient_name}</div>}
                      {isDirect && m.email_or_phone && <div className="text-xs text-[#777] mt-0.5">{m.email_or_phone}</div>}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => handleTest(m)} className="p-2 rounded-sm text-[#AFAFAF] hover:text-[#D4AF37] hover:bg-[#1A1A1A] transition-colors" title="Test" data-testid={`test-method-${m.id}`}>
                        {isDirect ? <Eye className="w-4 h-4" /> : <ExternalLink className="w-4 h-4" />}
                      </button>
                      <button onClick={() => handleEdit(m)} className="p-2 rounded-sm text-[#AFAFAF] hover:text-[#C6A66B] hover:bg-[#1A1A1A] transition-colors" title="Edit" data-testid={`edit-method-${m.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(m.id)} className="p-2 rounded-sm text-[#AFAFAF] hover:text-[#C44545] hover:bg-[#1A1A1A] transition-colors" title="Delete" data-testid={`delete-method-${m.id}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Test Modal (for direct/Zelle methods) */}
      {testModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setTestModal(null)} data-testid="test-modal">
          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-5">
              <PaymentProviderLogo provider={testModal.payment_provider || getProviderFromMethod(testModal.method)} method={testModal.method} size="lg" />
              <div>
                <div className="text-base font-bold text-[#EAE7E1]">{testModal.method || 'Payment Method'}</div>
                <div className="text-xs text-[#777]">Preview — this is how guests see it</div>
              </div>
            </div>
            <div className="space-y-3">
              {testModal.recipient_name && (
                <div className="flex items-center gap-3 p-3 bg-[#050505] rounded-sm border border-[#1A1A1A]">
                  <User className="w-4 h-4 text-[#777]" />
                  <div><div className="text-[10px] text-[#555] uppercase">Recipient</div><div className="text-sm text-[#EAE7E1] font-medium">{testModal.recipient_name}</div></div>
                </div>
              )}
              {testModal.email_or_phone && (
                <div className="flex items-center gap-3 p-3 bg-[#050505] rounded-sm border border-[#1A1A1A]">
                  <Phone className="w-4 h-4 text-[#777]" />
                  <div><div className="text-[10px] text-[#555] uppercase">Email / Phone</div><div className="text-sm text-[#EAE7E1] font-mono">{testModal.email_or_phone}</div></div>
                </div>
              )}
              {testModal.suggested_memo && (
                <div className="flex items-center gap-3 p-3 bg-[#050505] rounded-sm border border-[#1A1A1A]">
                  <Tag className="w-4 h-4 text-[#777]" />
                  <div><div className="text-[10px] text-[#555] uppercase">Memo</div><div className="text-sm text-[#D4AF37] italic">"{testModal.suggested_memo}"</div></div>
                </div>
              )}
              {testModal.qr_code_image && (
                <div className="flex justify-center p-3">
                  <SafeImage src={`${BACKEND_URL}${testModal.qr_code_image}`} alt="QR" className="h-32 w-32 rounded" imgClassName="bg-white p-1 object-contain border border-[#1A1A1A]" fallbackLabel="QR" />
                </div>
              )}
              {testModal.instructions && (
                <div className="text-sm text-[#777] italic border-t border-[#1A1A1A] pt-3">{testModal.instructions}</div>
              )}
            </div>
            <Button onClick={() => setTestModal(null)} className="w-full mt-5 bg-[#1A1A1A] hover:bg-[#222] text-[#EAE7E1] rounded-sm" data-testid="close-test-modal">
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
